import type { JSONContent } from "@tiptap/react"
import { sanitizeUrl, telegramMarkdownToHtml } from "./telegram-markdown-html"

/**
 * Перевод текста между редактором и тем, как он хранится. Тексты бота и рассылок лежат
 * в HTML Telegram (parse_mode=HTML), посты канала в своей разметке (**, __, ++ и так
 * далее). Редактор показывает форматирование сразу, а сохраняется прежний формат, поэтому
 * api, бот и превью ничего не замечают. Перенос строки везде просто \n.
 */
export type RichTextFormat = "telegram-html" | "post-markdown"

type MarkName = "link" | "bold" | "italic" | "underline" | "strike" | "spoiler" | "code"
type StyleMark = Exclude<MarkName, "link">

const HTML_OPEN: Record<StyleMark, string> = {
	bold: "<b>",
	italic: "<i>",
	underline: "<u>",
	strike: "<s>",
	spoiler: "<tg-spoiler>",
	code: "<code>",
}
const MARKDOWN_DELIMITER: Record<StyleMark, string> = {
	bold: "**",
	italic: "__",
	underline: "++",
	strike: "~~",
	spoiler: "||",
	code: "`",
}

interface Mark {
	type: MarkName
	attrs?: { href?: string }
}

/** Внешние метки раньше внутренних: ссылка снаружи, код внутри всего. */
const MARK_ORDER: MarkName[] = ["link", "bold", "italic", "underline", "strike", "spoiler", "code"]

const MARK_BY_TAG: Record<string, MarkName> = {
	b: "bold",
	strong: "bold",
	i: "italic",
	em: "italic",
	u: "underline",
	ins: "underline",
	s: "strike",
	strike: "strike",
	del: "strike",
	code: "code",
	"tg-spoiler": "spoiler",
	"spoiler-span": "spoiler",
}

const paragraph = (content: JSONContent[]): JSONContent =>
	content.length ? { type: "paragraph", content } : { type: "paragraph" }

/** Блоки документа из HTML: строки становятся абзацами, цитата и код отдельными блоками. */
function htmlToDoc(html: string, mergeQuotes: boolean): JSONContent {
	const body = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html").body
	return { type: "doc", content: readBlocks(body, mergeQuotes) }
}

function readBlocks(root: Node, mergeQuotes: boolean): JSONContent[] {
	const blocks: JSONContent[] = []
	let inline: JSONContent[] = []
	// Перенос сразу после цитаты или кода только отделяет их от следующей строки.
	let afterBlock = false

	const newline = () => {
		if (afterBlock) {
			afterBlock = false
			return
		}
		blocks.push(paragraph(inline))
		inline = []
	}
	const pushText = (text: string, marks: Mark[]) => {
		text.split("\n").forEach((part, index) => {
			if (index > 0) newline()
			if (!part) return
			afterBlock = false
			inline.push(marks.length ? { type: "text", text: part, marks } : { type: "text", text: part })
		})
	}
	const pushBlock = (block: JSONContent) => {
		if (inline.length) {
			blocks.push(paragraph(inline))
			inline = []
		}
		const previous = blocks.at(-1)
		if (mergeQuotes && block.type === "blockquote" && previous?.type === "blockquote" && afterBlock)
			previous.content = [...(previous.content ?? []), ...(block.content ?? [])]
		else blocks.push(block)
		afterBlock = true
	}

	const walk = (node: Node, marks: Mark[]) => {
		if (node.nodeType === Node.TEXT_NODE) {
			pushText(node.textContent ?? "", marks)
			return
		}
		if (!(node instanceof Element)) return
		const tag = node.tagName.toLowerCase()
		if (tag === "br") {
			newline()
			return
		}
		if (tag === "pre") {
			const text = (node.textContent ?? "").replace(/^\n+|\n+$/g, "")
			pushBlock(
				text ? { type: "codeBlock", content: [{ type: "text", text }] } : { type: "codeBlock" },
			)
			return
		}
		if (tag === "blockquote") {
			const inner = readBlocks(node, false)
			pushBlock({ type: "blockquote", content: inner.length ? inner : [paragraph([])] })
			return
		}
		let mark: Mark | null = null
		if (tag === "a") {
			const href = node.getAttribute("href")
			if (href) mark = { type: "link", attrs: { href } }
		} else if (tag === "span" && node.classList.contains("tg-spoiler")) {
			mark = { type: "spoiler" }
		} else if (MARK_BY_TAG[tag]) {
			mark = { type: MARK_BY_TAG[tag] }
		}
		const next = mark && !marks.some((item) => item.type === mark.type) ? [...marks, mark] : marks
		for (const child of node.childNodes) walk(child, next)
	}

	for (const child of root.childNodes) walk(child, [])
	if (inline.length || !afterBlock) blocks.push(paragraph(inline))
	return blocks
}

export function parseRichText(value: string, format: RichTextFormat): JSONContent {
	return format === "telegram-html"
		? htmlToDoc(value, false)
		: htmlToDoc(telegramMarkdownToHtml(value), true)
}

const escapeHtml = (text: string) =>
	text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

const sameMark = (a: Mark, b: Mark) => a.type === b.type && a.attrs?.href === b.attrs?.href

function openMark(mark: Mark, format: RichTextFormat): string {
	if (mark.type === "link")
		return format === "telegram-html"
			? `<a href="${escapeHtml(mark.attrs?.href ?? "").replace(/"/g, "&quot;")}">`
			: "["
	return format === "telegram-html" ? HTML_OPEN[mark.type] : MARKDOWN_DELIMITER[mark.type]
}

function closeMark(mark: Mark, format: RichTextFormat): string {
	if (mark.type === "link")
		return format === "telegram-html" ? "</a>" : `](${mark.attrs?.href ?? ""})`
	return format === "telegram-html"
		? HTML_OPEN[mark.type].replace("<", "</")
		: MARKDOWN_DELIMITER[mark.type]
}

/** Строка из текста с метками: метки открываются и закрываются стеком, без лишних повторов. */
/**
 * Пробел по краю размеченного куска выносится наружу: `**текст **` и `||спойлер ||` не
 * разметка ни в Telegram, ни в канале. Пробел не пропадает, а остаётся рядом, без тех
 * отметок, которые на нём заканчиваются: у края с соседом остаются только общие с ним.
 * Выделение двойным щелчком захватывает и пробел после слова, поэтому без этого почти
 * каждый формат, выбранный мышью, ломался бы.
 */
function liftEdgeSpaces(nodes: JSONContent[]): JSONContent[] {
	const marksOf = (node: JSONContent | undefined) =>
		node?.type === "text" ? ((node.marks ?? []) as Mark[]) : []
	const shared = (own: Mark[], other: Mark[]) =>
		own.filter((mark) => other.some((candidate) => sameMark(mark, candidate)))
	const out: JSONContent[] = []
	nodes.forEach((node, index) => {
		const marks = marksOf(node)
		if (node.type !== "text" || !node.text || marks.length === 0) {
			out.push(node)
			return
		}
		const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(node.text)
		const [, lead = "", body = "", trail = ""] = match ?? []
		const piece = (text: string, pieceMarks: Mark[]) => {
			if (!text) return
			out.push(pieceMarks.length ? { ...node, text, marks: pieceMarks } : { type: "text", text })
		}
		if (!body) {
			// Кусок из одних пробелов: отметки только те, что идут дальше с обеих сторон.
			piece(node.text, shared(shared(marks, marksOf(nodes[index - 1])), marksOf(nodes[index + 1])))
			return
		}
		piece(lead, shared(marks, marksOf(nodes[index - 1])))
		piece(body, marks)
		piece(trail, shared(marks, marksOf(nodes[index + 1])))
	})
	return out
}

function serializeInline(rawNodes: JSONContent[] | undefined, format: RichTextFormat): string {
	const nodes = liftEdgeSpaces(rawNodes ?? [])
	let out = ""
	const stack: { mark: Mark; start: number }[] = []
	const close = (depth: number) => {
		while (stack.length > depth) {
			const top = stack.pop()
			if (!top) break
			const inner = out.slice(top.start + 1)
			// Голый адрес в посте остаётся голым: превью и канал делают его ссылкой сами.
			if (
				format === "post-markdown" &&
				top.mark.type === "link" &&
				inner === top.mark.attrs?.href
			) {
				out = out.slice(0, top.start) + inner
				continue
			}
			out += closeMark(top.mark, format)
		}
	}
	for (const node of nodes) {
		if (node.type === "hardBreak") {
			close(0)
			out += "\n"
			continue
		}
		if (node.type !== "text" || !node.text) continue
		const marks = ((node.marks ?? []) as Mark[])
			.filter((mark) => MARK_ORDER.includes(mark.type))
			.sort((a, b) => MARK_ORDER.indexOf(a.type) - MARK_ORDER.indexOf(b.type))
		let common = 0
		while (
			common < stack.length &&
			common < marks.length &&
			sameMark(stack[common].mark, marks[common])
		)
			common++
		close(common)
		for (const mark of marks.slice(common)) {
			stack.push({ mark, start: out.length })
			out += openMark(mark, format)
		}
		out += format === "telegram-html" ? escapeHtml(node.text) : node.text
	}
	close(0)
	return out
}

export function serializeRichText(doc: JSONContent, format: RichTextFormat): string {
	return (doc.content ?? [])
		.map((block) => {
			if (block.type === "blockquote") {
				const lines = (block.content ?? []).map((line) => serializeInline(line.content, format))
				return format === "telegram-html"
					? `<blockquote>${lines.join("\n")}</blockquote>`
					: lines.map((line) => `>${line}`).join("\n")
			}
			if (block.type === "codeBlock") {
				const text = (block.content ?? []).map((node) => node.text ?? "").join("")
				return format === "telegram-html"
					? `<pre>${escapeHtml(text)}</pre>`
					: `\`\`\`\n${text}\n\`\`\``
			}
			return serializeInline(block.content, format)
		})
		.join("\n")
}

/** Адрес ссылки из поля ввода: только http, https и tg, иначе null. */
export function normalizeLinkInput(raw: string): string | null {
	const trimmed = raw.trim()
	if (!trimmed) return null
	return sanitizeUrl(/^(https?:|tg:)/i.test(trimmed) ? trimmed : `https://${trimmed}`)
}
